import { createSelector, createSlice } from "@reduxjs/toolkit";
import {
  getDomainById,
  getDomainsByType,
  enrollUserInDomain,
  getJobCourses,
} from "../../services/domainService";
import { STATUS } from "../../constants/statuses";
import _ from "lodash";
import {
  selectAllDomainSkillPaths,
  skillPathActions,
} from "./skillPathReducer";
import { courseActions, selectAllCourses } from "./courseReducer";
import { launchPadLayoutActions } from "./layoutReducer";
import { DOMAIN_TYPES } from "../../constants/domainTypes";

const domainSlice = createSlice({
  name: "domains",
  initialState: [],
  reducers: {
    removeDomainByType(state, action) {
      return state.filter((domain) => domain.domainTypeId != action.payload);
    },
    addDomains(state, action) {
      state.push(...action.payload);
    },
    startDomain(state, action) {
      let stateDomain = state.find((y) => y.id === action.payload);
      if (stateDomain) {
        stateDomain.status = STATUS.IN_PROGRESS;
      }
    },
    completeDomains(state, action) {
      action.payload.forEach((id) => {
        let stateDomain = state.find((y) => y.id === id);
        if (stateDomain) {
          stateDomain.completedCourses = stateDomain.totalCourses;
          stateDomain.completedDuration = stateDomain.totalDuration;
          stateDomain.status = STATUS.COMPLETED;
        }
      });
    },
    updateDomains(state, action) {
      action.payload.forEach((x) => {
        let stateDomain = state.find((y) => y.id === x.id);
        if (stateDomain) {
          stateDomain.completedCourses = x.completedCourses;
          stateDomain.completedDuration = x.completedDuration;
        }
      });
    },
  },
});

export const completeDomains = (domains) => {
  return (dispatch) => {
    // Add new completed job domains in an array to show the completion modal for each one
    const newDomains = domains.map((id) => ({ id }));
    dispatch(launchPadLayoutActions.setPendingCompletedJobDomains(newDomains));
    dispatch(domainActions.completeDomains(domains));
  };
};

export const removeDomainsByType = (domainTypeId) => {
  return (dispatch, getState) => {
    const { domains } = getState();
    const { skillPaths } = getState();

    let domainIdsToRemove = domains
      .filter((x) => x.domainTypeId === domainTypeId)
      .map((x) => x.id);

    const skillPathIdsToRemove = skillPaths
      .filter((x) =>
        Object.keys(x.domainInfo).some((y) => domainIdsToRemove.includes(y))
      )
      .map((x) => x.id);

    if (domainTypeId == DOMAIN_TYPES.USER_SELECTED_JOB) {
      dispatch(courseActions.removeUserCourses());
    }
    dispatch(courseActions.removeCoursesSkillPathInfo(skillPathIdsToRemove));
    dispatch(skillPathActions.removeSkillPathDomainInfo(domainIdsToRemove));
    dispatch(domainActions.removeDomainByType(domainTypeId));
  };
};

//fetch and cache domains by type
export const getAllDomains = (domainType) => {
  return async (dispatch, getState) => {
    const { domains } = getState();

    let userDomains = domains.filter((x) => x.domainTypeId === domainType);

    if (userDomains.length === 0) {
      let response = await getDomainsByType(domainType);
      dispatch(domainActions.addDomains(response.data));
    }
  };
};

//fetch and cache single domain
export const getDomain = (id) => {
  return async (dispatch, getState) => {
    const { domains } = getState();

    if (!domains.find((x) => x.id === id)) {
      let domain = await getDomainById(id);
      dispatch(domainActions.addDomains([domain.data]));
    }
  };
};

export const enrollInDomain = (domainId) => {
  return async (dispatch) => {
    //api request to enroll user in domain
    //then when the request succeeds , we update
    //the redux domain status
    await enrollUserInDomain(domainId);
    dispatch(domainActions.startDomain(domainId));
  };
};

//fetch and cache job details
export const getJobCoursesWithSkills = (id) => {
  return async (dispatch, getState) => {
    const { skillPaths } = getState();

    let domainSkillPaths = skillPaths.filter((x) => x.domainInfo[id]);

    if (domainSkillPaths.length === 0) {
      let response = await getJobCourses(id);

      let courses = [];
      let skillPaths = _.chain(response.data)
        .groupBy("skillPathId")
        .map((value, key, index) => {
          let skillPath = value[0];
          let skillPathCourses = value.map((course) => ({
            id: course.id,
            code: course.code,
            name: course.name,
            summary: course.summary,
            description: course.description,
            duration: course.duration,
            thumbnail: course.thumbnail,
            link: course.link,
            status: course.status,
            rank: course.rank,
            required: course.required,
            //all courses are unlocked for job
            locked: 0,
            skillPathId: key,
            contentSkills: course.contentSkills,
          }));

          courses.push(...skillPathCourses);
          return {
            skillPathId: key,
            skillPath: skillPath.skillPathName,
            skillPathDescription: skillPath.skillPathDescription,
            skillPathLevelId: skillPath.skillPathLevelId,
            skillPathRank: index,
            domainId: id,
          };
        })
        .value();

      dispatch(
        skillPathActions.upsertSkillPaths({
          skillPaths: skillPaths,
          refetch: false,
        })
      );
      dispatch(courseActions.upsertCourses(courses));
    }
  };
};

//-------------------------------------------------- Selectors
const selectAllDomains = (state) => state.domains;

export const selectDomainsByType = createSelector(
  [selectAllDomains, (state, type) => type],
  (domains, type) => {
    return domains.filter((x) => x.domainTypeId === type);
  }
);

export const selectDomainSkills = createSelector(
  [selectAllDomainSkillPaths, selectAllCourses],
  (domainSkillPaths, courses) => {
    let domainSkills = [];
    let domainCourses = courses.filter((x) =>
      domainSkillPaths.some((sp) => x.skillPathInfo[sp.id])
    );

    domainCourses.forEach((course) => {
      //display only skills that are found in success profile
      domainSkills.push(
        ...course.skills.filter((sk) => sk.belongsToSuccessProfile)
      );
    });

    return _.uniqBy(domainSkills, (x) => x.name).sort(
      (a, b) => a.rank - b.rank
    );
  }
);

export const domainActions = domainSlice.actions;

export default domainSlice.reducer;
